<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { getReleaseLogLink } from '@/shared/helpers/routing';
    import ExportButton from '@/shared/components/ExportButton.vue';
    import EditCodeSet from './EditCodeSet.vue';
    import SpecializedUiButton from '@/shared/components/SpecializedUiButton.vue';
    import LibraryDocumentationButton from './LibraryDocumentationButton.vue';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import PublishCodeSetButton from '@/shared/components/PublishCodeSetButton.vue';
    import CodeSetCopyButton from './CodeSetCopyButton.vue';
    import { getUnsupportedQueryMessage } from './CodeGridFilters';
    import type { CodeSetProp } from '@/shared/helpers/api/types/entities';

    const props = withDefaults(defineProps<{
        codeSet?: CodeSetProp | null;
        filteredCodeCount?: number | null;
        codeCount: number;
        queries: { regEx: string[]; queries: string[][] };
        exportColumns?: string[] | null;
        editorActive?: boolean;
    }>(), {
        codeSet: null,
        filteredCodeCount: null,
        exportColumns: null,
        editorActive: false
    });

    const emit = defineEmits<{
        'update:editorActive': [value: boolean];
        'refresh': [];
    }>();

    const modalActive = ref(false);

    const library = computed(() => props.codeSet ? props.codeSet.library : null);

    const exportFilters = computed(() => ({
        RegEx: props.queries.regEx,
        Queries: props.queries.queries,
        Columns: props.exportColumns
    }));

    const publishFilters = computed(() => ({
        regEx: props.queries.regEx,
        queries: props.queries.queries
    }));

    const scopes = computed(() => props.codeSet ? props.codeSet.scopes : null);

    const hasScopes = computed(() => props.codeSet && props.codeSet.scopes && props.codeSet.scopes.length > 0);

    // The grid can filter in ways the API cannot run, and those must not be sent at all.
    const unsupportedQueryMessage = computed(() => getUnsupportedQueryMessage(props.queries.queries));

    function reload() {
        modalActive.value = false;
        emit('update:editorActive', false);
        emit('refresh');
    }

    function onClose() {
        modalActive.value = false;
    }
</script>

<template>
    <div>
        <div v-if="codeSet">
            <b-notification
                v-show="codeSet && !codeSet.isReleaseReady"
                type="is-warning"
                :closable="false"
                has-icon
                role="alert">
                <p><b>Not release ready</b></p>
                <p>
                    This library set has not been marked as ready for release. The content will not be communicated to other systems.
                </p>
            </b-notification>

            <b-notification
                v-show="codeSet && !codeSet.lockedForDelete"
                type="is-warning"
                :closable="false"
                has-icon
                role="alert">
                <p><b>Items may be deleted</b></p>
                <p>
                    This set is opened so that items may be deleted. This generally means the set is <u>not production ready</u>, and it must be locked for deletion when appropriate.
                </p>
            </b-notification>

            <div class="columns">
                <div class="column">
                    <h1 class="title is-4">
                        {{ codeSet.description }}
                    </h1>
                    <h2 class="subtitle is-6">
                        Number of items: <b>{{ filteredCodeCount + ' / ' + codeCount }}</b>
                    </h2>
                </div>
                <div class="column is-narrow is-flex-shrink-0 is-flex is-justify-content-flex-end">
                    <b-field>
                        <p class="control">
                            <library-documentation-button
                                :library-name="library?.name"
                                :definition="codeSet?.definition"
                                button-type="" />
                        </p>
                        <p class="control">
                            <export-button
                                :disabled="(!hasScopes && !library?.isGlobal) || !!unsupportedQueryMessage"
                                :code-set-name="codeSet.name"
                                :code-set-description="codeSet.description"
                                :code-filter="exportFilters" />
                        </p>
                        <p class="control">
                            <publish-code-set-button
                                :library-name="library?.name ?? ''"
                                :code-set-name="codeSet.name"
                                :code-filter="publishFilters"
                                :filtered-code-count="filteredCodeCount ?? 0"
                                :disabled="!!unsupportedQueryMessage" />
                        </p>
                        <p
                            v-if="library && !library.isGlobal"
                            class="control">
                            <code-set-copy-button
                                :library-name="library.name"
                                :code-set-name="codeSet.name"
                                :code-filter="publishFilters"
                                :filtered-code-count="filteredCodeCount ?? 0"
                                :disabled="!!unsupportedQueryMessage" />
                        </p>
                        <p class="control">
                            <router-link
                                :to="getReleaseLogLink({codeSetName: codeSet.name})"
                                class="button">
                                <b-icon
                                    icon="format-list-bulleted"
                                    size="is-small" />
                                <span>Log</span>
                            </router-link>
                        </p>
                        <p class="control">
                            <b-button
                                v-require-can-edit-code="{ libraryName: codeSet.library.name, scopes: codeSet.scopes }"
                                class=""
                                @click="modalActive = true">
                                <b-icon
                                    icon="cog"
                                    size="is-small" />
                                <span>Settings</span>
                            </b-button>
                        </p>
                        <p
                            v-if="!editorActive"
                            class="control">
                            <specialized-ui-button
                                button-icon="plus"
                                button-label="Add new item"
                                dropdown-text="Add new item using code editor"
                                :code-set="codeSet"
                                :library="library"
                                :disabled="!hasScopes && !library?.isGlobal"
                                @clickEdit="emit('update:editorActive', true)" />
                        </p><p
                            v-else
                            class="control">
                            <b-button
                                label="Cancel"
                                icon-left="close-circle"
                                @click="emit('update:editorActive', false)" />
                        </p>
                    </b-field>
                </div>
            </div>

            <b-notification
                v-if="unsupportedQueryMessage"
                type="is-warning"
                :closable="false"
                has-icon
                role="alert">
                {{ unsupportedQueryMessage }}
            </b-notification>

            <b-modal
                v-model="modalActive"
                :width="1600"
                :can-cancel="['escape']"
                has-modal-card>
                <edit-code-set
                    :codeset="codeSet"
                    :library="library!"
                    :scopes="scopes ?? []"
                    @refresh="reload"
                    @close="onClose" />
            </b-modal>
        </div>

        <hr>
    </div>
</template>
